import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./DeleteAccountModal.css";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

/**
 * Danger zone for irreversible account actions in settings.
 *
 * @example
 * <ActionItemsSettingsDangerSection
 *   userEmail="abhinav@starmesh.ai"
 *   onDeleteAccount={async () => { await deleteAccount(); }}
 * />
 */
const ActionItemsSettingsDangerSection = ({
  userEmail = "",
  onDeleteAccount,
  compact = false,
  embedded = false,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const expectedEmail = normalizeEmail(userEmail);
  const emailMatches = normalizeEmail(emailConfirm) === expectedEmail;

  useEffect(() => {
    if (!confirmOpen) {
      return undefined;
    }

    setEmailConfirm("");
    setConfirmError("");

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !busy) {
        setConfirmOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [busy, confirmOpen]);

  const handleDeleteClick = () => {
    setError("");
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (busy) return;
    setConfirmOpen(false);
  };

  const handleConfirmDelete = async (event) => {
    event.preventDefault();
    if (!emailMatches) {
      setConfirmError(
        expectedEmail
          ? "Email does not match your account."
          : "Enter your account email to confirm deletion."
      );
      return;
    }

    setBusy(true);
    setConfirmError("");
    setError("");
    try {
      await onDeleteAccount?.();
    } catch (err) {
      const message = String(err?.message || "Failed to delete account");
      setError(message);
      setConfirmError(message);
      setBusy(false);
    }
  };

  const sectionClass = [
    "settings-danger-zone",
    compact ? "settings-danger-zone--compact" : "",
    embedded ? "settings-danger-zone--embedded" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={sectionClass}
      aria-label={embedded ? "Danger zone" : undefined}
      aria-labelledby={embedded ? undefined : "settings-danger-zone-title"}
    >
      {!embedded ? (
        <h3 id="settings-danger-zone-title" className="settings-danger-zone__title">
          Danger zone
        </h3>
      ) : null}
      <p className="settings-danger-zone__desc">
        Permanently delete your Task Mantis account and all associated data, including connected
        apps, action items, and saved settings.
      </p>
      {error ? <p className="settings-danger-zone__error">{error}</p> : null}
      <button
        type="button"
        className="settings-danger-zone__btn"
        onClick={handleDeleteClick}
        disabled={busy}
      >
        {busy ? "Deleting account…" : "Delete account"}
      </button>

      {confirmOpen
        ? createPortal(
            <div
              className="delete-account-modal__backdrop"
              onClick={handleCloseConfirm}
            >
              <div
                className="delete-account-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-account-title"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="delete-account-modal__close"
                  onClick={handleCloseConfirm}
                  disabled={busy}
                  aria-label="Close delete account dialog"
                >
                  ×
                </button>
                <h3 id="delete-account-title" className="delete-account-modal__title">
                  Delete your account?
                </h3>
                <p className="delete-account-modal__desc">
                  This permanently removes your profile, connected apps, action items, and all
                  saved settings. This cannot be undone.
                </p>
                <form className="delete-account-modal__form" onSubmit={handleConfirmDelete}>
                  <label className="delete-account-modal__field">
                    <span>Type your email to confirm</span>
                    {userEmail ? (
                      <p className="delete-account-modal__email-hint">{userEmail}</p>
                    ) : null}
                    <input
                      type="email"
                      className="delete-account-modal__input"
                      value={emailConfirm}
                      onChange={(event) => {
                        setEmailConfirm(event.target.value);
                        if (confirmError) setConfirmError("");
                      }}
                      autoComplete="email"
                      autoFocus
                      disabled={busy}
                      placeholder={userEmail || "you@company.com"}
                    />
                  </label>
                  {confirmError ? (
                    <p className="delete-account-modal__error">{confirmError}</p>
                  ) : null}
                  <div className="delete-account-modal__actions">
                    <button
                      type="submit"
                      className="delete-account-modal__confirm"
                      disabled={busy || !emailMatches}
                    >
                      {busy ? "Deleting account…" : "Delete account"}
                    </button>
                    <button
                      type="button"
                      className="delete-account-modal__cancel"
                      onClick={handleCloseConfirm}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
};

export default ActionItemsSettingsDangerSection;
